/**
 * The glossary (DESIGN UD-18, P2): one entry per term the interface defines in place. Rendered by `Term` popovers, the
 * Glossary view and the exports' appendix. Plain constants, no DOM, so the worker's report builders and the word test
 * (test/unit/help-strings.test.ts) can read them. None may contain a word matched by `BANNED_WORDS` (UX-WORD-01).
 * Voice: plain, short, specific; numbers with their units; no exclamation marks. The numbers quoted here come from the
 * model card of `refit-gam-3c8d2de6` (the default since 2.25.0, 2026-10-21) and the bench pilot (2026-10-03); update them when
 * those change.
 */
import { CHANNEL_IDS, TR_CHANNEL_IDS } from '../engine/channels/types.ts';
import { GLOSSARY_KEYS, type GlossaryEntry, type GlossaryKey } from '../shared/help-types.ts';

const N = CHANNEL_IDS.length;
const N_TR = TR_CHANNEL_IDS.length + 1;

export const GLOSSARY: Record<GlossaryKey, GlossaryEntry> = {
	/* ---------------- scores and intervals ---------------- */
	channel: {
		term: 'channel',
		definition: `One measurement of one writing habit, reported on its own: ${N} channels in all, ${N - N_TR} on the authorship axis and ${N_TR} on the translation axis.`,
		use: 'Channels are never merged; read them one by one, and read the passages they mark.',
	},
	'evidence-strength': {
		term: 'evidence strength',
		definition:
			'A channel’s value on a 0–100 scale: how far its measurement sits from the human reference, in the machine-like direction, given how much text supports it.',
		use: 'Around 20 is the human reference; a high value means the habit is strongly present, not that the text is machine-written.',
	},
	index: {
		term: 'index',
		definition:
			'A 0–100 value for the whole document, a section or a chapter that summarises the channel evidence. It is not a percentage of machine text. For the whole-document machine-authorship index, the dial reads it against the reference corpus at the reference prior, with its conditions.',
	},
	kappa: {
		term: 'data weight κ',
		definition:
			'How much text stands behind a channel’s value, from 0 to 1: the share of the estimate that comes from this document rather than from the prior.',
		use: 'Below 0.20 the row says “little evidence” and its whisker is hollow; weigh such a channel lightly.',
	},
	deviation: {
		term: 'deviation from the reference',
		definition:
			'How many standard units the channel’s measurement lies from the human reference, signed so that positive is machine-like whichever way the raw value runs.',
		use: 'Compare channels by deviation when their units differ; a large negative value is a strongly human-like reading.',
	},
	'interval-90': {
		term: '90% interval',
		definition:
			'The range a value would fall in nine times out of ten if the same text were re-sampled; it covers sampling variation inside this document only, not errors in the model.',
		use: 'A wide interval means little text or an uneven document; do not read a difference smaller than the interval.',
	},
	'interval-method': {
		term: 'interval method',
		definition:
			'How a section or chapter interval was computed: “bootstrap” by re-sampling its paragraphs, “analytic” from a formula when there are too few paragraphs to re-sample.',
	},
	'text-sampling-variation': {
		term: 'text-sampling variation',
		definition:
			'The range the calibrated index moves over when the document’s chapters, scenes or paragraphs are re-sampled. It does not include the uncertainty of the calibration itself.',
		use: 'On the reference corpus it is too narrow (it covered an independent half of the same document in 77% to 87% of cases, against a nominal 90%), so it never decides the status.',
	},
	contribution: {
		term: 'contribution to the index sum L',
		definition:
			'This channel’s term in the seed rules’ weighted sum L, from which the seed-rules composite is formed: its weight times its deviation, damped for one-way channels.',
		use: 'It explains the composite, not the calibrated index, which is a separate model over all channels.',
	},
	'little-evidence': {
		term: 'little evidence',
		definition:
			'The note on a channel whose data weight κ is below 0.20: the text gave it too little to measure well, so its value leans on the prior.',
	},
	'hollow-whisker': {
		term: 'hollow whisker',
		definition:
			'An interval drawn with a hollow marker: the channel has little evidence (κ below 0.20). A filled marker means the estimate rests on enough text.',
	},

	/* ---------------- the headline ---------------- */
	calibrated: {
		term: 'calibrated',
		definition:
			'Scaled against a reference corpus of human and machine text, so that the model card can state measured error rates. Only the machine-authorship index is calibrated.',
	},
	'calibrated-confidence': {
		term: 'calibrated confidence (at the reference prior)',
		definition:
			'The index read as “N in 100”: among documents of our reference corpus that score like this one, about N in 100 were machine-written, assuming that half the documents are machine-written. It is a reading of the score on that corpus, not how sure to be that this one document is machine-written.',
		use: 'How sure to be about one document depends on how much machine text you receive. The corpus holds machine text from Anthropic models only, and the calibration error for the length group stands beside the reading. Only the authorship dial reads this way; the translation dial never does.',
	},
	uncalibrated: {
		term: 'uncalibrated',
		definition:
			'Not scaled against a reference corpus. The translation index, the seed-rules composite, the section strip and every channel value are uncalibrated: they measure machine-associated patterns and carry no error rate.',
	},
	'reference-point': {
		term: 'reference point',
		definition:
			'The index value above which the document is called “flagged”. It is set where, in the strictest human stratum of the reference corpus, 1% of human documents scored higher.',
		use: 'The model card measures 16 of 4,105 human documents flagged at its operating points, cross-validated (0.4%, upper bound 0.6%); the widest measured cell is period translations from Italian, 0 of 34 (upper bound 8.7%).',
	},
	'reference-prior': {
		term: 'reference prior',
		definition:
			'The share of machine text the calibration assumes, 0.50: half human, half machine. The dial reads the index at that prior; it is not a rate for any real mix of documents.',
	},
	'strictest-stratum': {
		term: 'strictest-stratum 1% false-positive point',
		definition:
			'The name of the reference point: the index value that the human stratum with the highest scores exceeds in 1% of its documents, so every stratum is at or under 1% at that point in cross-validation.',
	},
	'length-group': {
		term: 'length group',
		definition:
			'The band of prose-word counts the document falls in (B1 shortest to B6 longest); the calibration is fitted and its error rates are measured per group.',
		use: 'A group with fewer than 30 reference documents is unmeasured; the model card lists each.',
	},
	'evidence-coverage': {
		term: 'evidence coverage',
		definition:
			'How much channel evidence stands behind the index, from 0.10 to 1.00: from the text length and the channels’ data weights.',
		use: 'Low coverage means many channels abstained or had little text; read the notices before the number.',
	},
	'seed-rules-composite': {
		term: 'seed-rules composite',
		definition:
			'The earlier, uncalibrated 0–100 summary from the seed’s rule set: a weighted sum of channel deviations mapped to the scale. It is shown beside the calibrated index for continuity and is on another scale.',
		use: 'The composite and the section strip come from these rules, so they need not agree with the calibrated index.',
	},
	status: {
		term: 'status',
		definition:
			'Where the calibrated index sits against the reference point: flagged (above it), at the reference point, below it, in the abstention band, or not available (too little text or no calibrated model for this language).',
		use: 'The engine sets the status; the interface only prints it.',
	},
	'model-card': {
		term: 'model card',
		definition:
			'The document that describes the calibrated model: the reference corpus, the reference point, and the error rates per human stratum and per length group, each with its sample size and a one-sided 95% upper bound.',
	},

	'closest-categories': {
		term: 'closest human categories',
		definition:
			'The human categories of the reference corpus whose channel ranges this text fits best: for each category taken on its own, how many of the text’s measured channels (30 or more human documents of this length there) fall inside its range.',
		use: 'It is resemblance by ranges, uncalibrated and without an error rate; it does not say who wrote the text.',
	},
	'human-range': {
		term: 'human range',
		definition:
			'The middle 90% of a channel’s scores (5th to 95th percentile) among the human documents of about the text’s length, half to twice its words, in the chosen categories.',
		use: 'Green is within it, yellow and red are outside it; fewer than 30 documents of that length make the cell unmeasured.',
	},

	/* ---------------- flags and places ---------------- */
	hotspot: {
		term: 'hotspot',
		definition:
			'A stretch where a channel is significantly above both the human reference and the document’s own background, with false discoveries controlled at 5% across all channels.',
		use: 'The strongest kind of local evidence: open it in the reader and read the passage.',
	},
	elevated: {
		term: 'elevated',
		definition:
			'A stretch where a channel is significantly above the human reference only, not above the document’s own background.',
		use: 'Weaker than a hotspot: the habit is present here but no more than in the rest of the document.',
	},
	segment: {
		term: 'segment',
		definition:
			'A run of consecutive paragraphs over which a channel’s density is homogeneous, found by change-point detection; boundaries snap to the document’s own structure.',
	},
	'single-paragraph': {
		term: 'single paragraph',
		definition: 'A flagged entry that covers one paragraph: a local spike of the channel’s habit.',
	},
	'chapter-unit': {
		term: 'chapter',
		definition:
			'A natural unit of the document (a chapter, or a scene when the document has no chapters), used as a unit of measurement and of flagging.',
	},
	'joint-segment': {
		term: 'joint segment',
		definition:
			'A section of the document inside which the channels, taken together, keep the same heuristic profile. The strip shows one index per joint segment, aggregated on that section’s own text.',
	},
	'p-reference': {
		term: 'p (reference)',
		definition:
			'The p-value of the test that this stretch’s rate is above the human reference; small means sampling noise alone would rarely produce the excess.',
	},
	'p-background': {
		term: 'p (background)',
		definition:
			'The p-value of the test that this stretch’s rate is above the rest of the document; small means the stretch stands out from its own context.',
	},
	'rate-ratio-reference': {
		term: 'rate ratio (reference)',
		definition:
			'The stretch’s rate divided by the human reference rate; 2.0 means twice the reference.',
	},
	'rate-ratio-background': {
		term: 'rate ratio (background)',
		definition:
			'The stretch’s rate divided by the rate in the rest of the document; 2.0 means twice its surroundings.',
	},
	'engine-ranking': {
		term: 'strength (engine ranking)',
		definition:
			'The order the engine gives hotspot entries: by how far the stretch stands above the reference, with the better-supported entries first.',
	},
	'evidence-span': {
		term: 'evidence span',
		definition:
			'A marked run of text that a channel counted: a word, a phrase, a sentence opener or a construction. The reader underlines them; the inspector names the feature.',
	},
	'weight-zero': {
		term: 'evidence only (weight 0)',
		definition:
			'A span that is shown for the reader’s judgement but does not count toward the channel’s score, such as a spaced hyphen for the em-dash channel.',
	},
	feature: {
		term: 'feature',
		definition:
			'The kind of thing a channel counted within a span, named by the channel (for example “not … but …”).',
	},
	withheld: {
		term: 'flags withheld',
		definition:
			'The channel’s value is shown, but its hotspot and elevated flags are not, because its reference comes from the display ramp rather than from human text.',
	},
	abstained: {
		term: 'abstained',
		definition:
			'The channel did not report because the text gave it too little to measure; the reason is listed in Diagnostics. An abstention is not a clean result.',
	},
	'length-dependent': {
		term: 'length-dependent',
		definition:
			'The channel’s value changes with document length until its priors come from corpus values; weigh it lightly for a document of this length.',
	},
	'reads-other-way': {
		term: 'reads the other way at this length',
		definition:
			'In the reference corpus, at this document’s length group, the channel pointed the opposite way from its stated direction, or showed no direction.',
		use: 'Weigh this channel’s value lightly for a document of this length.',
	},
	'pattern-indicator': {
		term: 'pattern indicator',
		definition:
			'A rule output of the seed’s ensemble that fires on a combination of channels: an assistance-residue pattern or a machine-translation pattern. It is shown with the evidence that triggered it, never as a conclusion.',
	},
	'text-hygiene': {
		term: 'text hygiene',
		definition:
			'Invisible characters inside words, or Cyrillic letters that look like Latin ones, found and normalised before analysis. The notice counts them; it is evidence about the text, not a score.',
	},

	/* ---------------- families ---------------- */
	family: {
		term: 'family',
		definition:
			'A grouping label for channels that measure related habits (vocabulary, rhythm, reuse, …). Families group the table; they never replace channel results.',
	},
	'family-vocab': {
		term: 'Vocab',
		definition: 'Word choice: machine-favoured words, hedges, brand-like framework names.',
	},
	'family-formula': {
		term: 'Formula',
		definition:
			'Template phrases and sentence openers that scaffold a text (“Moreover,”, “It is worth noting”).',
	},
	'family-rhetoric': {
		term: 'Rhetoric',
		definition:
			'Argument moves: contrastive “not X, but Y”, absolutes and maxims, negation-led antithesis.',
	},
	'family-figure': {
		term: 'Figure',
		definition: 'Figures of speech and glosses: “the way” similes, term-and-gloss pairs.',
	},
	'family-choreo': {
		term: 'Choreo',
		definition:
			'How sentences and paragraphs are staged: triads, repeated openers, parallel frames, narrative beats, numbered scaffolding.',
	},
	'family-rhythm': {
		term: 'Rhythm',
		definition:
			'Patterns of sentence and paragraph length: how varied they are, how they alternate, how paragraphs close, how the cadence drifts.',
	},
	'family-prosody': {
		term: 'Prosody',
		definition: 'Marks of emphasis: em-dashes, dash pairs, italics.',
	},
	'family-reuse': {
		term: 'Reuse',
		definition:
			'Repetition within the document: repeated n-grams, verbatim motifs, templates re-filled with new words.',
	},
	'family-texture': {
		term: 'Texture',
		definition:
			'The small irregularities of human writing: names and numbers, contractions, typing slips, colloquial fillers. These channels run the other way: fewer irregularities is more machine-like.',
	},
	'family-envelope': {
		term: 'Envelope',
		definition: 'The shape of the whole: how uniform the chapter lengths are.',
	},
	'family-residue': {
		term: 'Residue',
		definition:
			'Traces of a pipeline: markup left in plain text, citation tokens, chat residue, prompt text.',
	},
	'family-segment': {
		term: 'Segment',
		definition:
			'How the document splits: whether its units fall into two populations of different lean.',
	},
	'family-translation': {
		term: 'Translation',
		definition: 'The translation axis: habits of English produced by machine translation.',
	},

	/* ---------------- bench ---------------- */
	stretch: {
		term: 'stretch',
		definition:
			'The bench’s unit of rewriting: a run of paragraphs targeted together, cut at paragraph boundaries around the flagged evidence.',
	},
	'local-pass': {
		term: 'local pass',
		definition:
			'Fixed text substitutions run in the browser with no key and no network call: phrase and word swaps, scaffolding words, dashes, Markdown residue, quotation marks. It reports its effect per channel.',
	},
	'deep-rewrite': {
		term: 'deep rewrite',
		definition:
			'Sends each targeted stretch to the model, up to three attempts, and keeps an attempt only if it reduces the targeted evidence, adds no new hotspot elsewhere and keeps the meaning. Your file is not changed; export the rewritten text separately.',
	},
	economy: {
		term: 'Economy',
		definition:
			'Sends the requests of each round to Anthropic in one batch through the app’s relay, at about half the price. Up to three rounds; each round can take up to 24 hours; the job resumes if you close the page.',
	},
	'standard-key': {
		term: 'Standard (your own key)',
		definition:
			'The browser calls Anthropic directly with your API key, one request at a time, at the list price. The key is used for nothing else.',
	},
	'kept-best': {
		term: 'kept best',
		definition:
			'No attempt passed every check, so the closest attempt that was safe was kept. It is not a full fix; you can revert it.',
	},
	'dialogue-checks': {
		term: 'dialogue checks',
		definition:
			'A guard that a rewritten passage keeps its quoted speech: who speaks, how much, and what is said.',
	},
	'meaning-review': {
		term: 'meaning review',
		definition:
			'Under the editorial guardrails, a second model call reads each rewrite beside the original and checks that it keeps the meaning (what may or must happen, who acts, what is denied, when, who knows it, what causes what) and that the editor’s stated reason names a real benefit to the reader. A rewrite that fails is not accepted; the editor tries again or leaves the passage as it is.',
	},
	'signal-score': {
		term: 'signal score S',
		definition:
			'The net effect of an edit on the passage’s evidence, from −1 to +1: R is the points of evidence removed, I the points added, B the points the passage carried before. +1 means all of it gone and nothing added; 0 no net change; below 0 more added than removed.',
	},
	ledger: {
		term: 'ledger',
		definition:
			'The record of every attempt, acceptance and reversion in a rewrite job; it lets the job resume and lets you revert any stretch.',
	},
	'reasoning-tokens': {
		term: 'reasoning tokens',
		definition:
			'Tokens the model spends thinking before it answers. They are billed as output, so the cost estimate includes them.',
	},
	effort: {
		term: 'effort',
		definition:
			'How much reasoning the model is asked to spend per request, from Low to Maximum. More effort costs more and takes longer; the bench pilot measured Low, Medium and High.',
	},
};

export function glossaryFor(k: GlossaryKey): GlossaryEntry {
	return GLOSSARY[k];
}

/** Every entry in the order of `GLOSSARY_KEYS` (the Glossary view and the exports’ appendix). */
export function glossaryEntries(): Array<[GlossaryKey, GlossaryEntry]> {
	return GLOSSARY_KEYS.map((k) => [k, GLOSSARY[k]]);
}
